import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Calendar, User, Mail, Briefcase, Building2 } from "lucide-react";
import { Spinner } from "../components/ui/shadcn-io/spinner";

interface Employee {
  _id: string;
  fullName: string;
  email: string;
  designation: string;
  department: string;
  company: string;
  employeeType: string;
}

interface Note {
  _id: string;
  date: string;
  content: string;
  employee: Employee;
  createdAt: string;
  updatedAt: string;
}

export default function AdminNotesDetail() {
  const navigate = useNavigate();
  const { date } = useParams<{ date: string }>();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (date) {
      fetchNotesForDate();
    }
  }, [date]);

  const fetchNotesForDate = async () => {
    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/notes/admin/all?startDate=${date}&endDate=${date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotes(response.data.notes || []);
    } catch (error: any) {
      console.error("Error fetching notes:", error);
      setError(error.response?.data?.message || "Failed to fetch notes");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "EEEE, MMMM d, yyyy");
  };

  const formatDateTime = (dateStr: string) => {
    return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/notes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Notes for {date ? formatDate(date) : "Selected Date"}
          </h2>
          <p className="text-muted-foreground flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4" />
            {notes.length} {notes.length === 1 ? "note" : "notes"} found
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {notes.length === 0 && !error ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Notes Found</h3>
              <p className="text-muted-foreground">
                There are no notes from any employees for this date.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <Card key={note._id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">
                      {note.employee.fullName}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span>{note.employee.email}</span>
                      </div>
                      {note.employee.designation && (
                        <div className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          <span>{note.employee.designation}</span>
                        </div>
                      )}
                      {note.employee.department && (
                        <Badge variant="outline" className="text-xs">
                          {note.employee.department}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      Created: {formatDateTime(note.createdAt)}
                    </p>
                    {note.updatedAt !== note.createdAt && (
                      <p className="text-xs text-muted-foreground">
                        Updated: {formatDateTime(note.updatedAt)}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {note.content}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

